import { Skeleton } from "./Skeleton"

interface ActivityRowSkeletonProps {
	variant?: "profile" | "market"
}

/**
 * ActivityRowSkeleton - Skeleton for activity feed rows
 *
 * Matches actual row dimensions exactly to prevent layout shift:
 * - "profile": Card-based with sm avatar (32px), matches ProfileActivityRow
 * - "market": Matches TradeRow structure (sm avatar, inline text, external link icon)
 */
export function ActivityRowSkeleton({ variant = "profile" }: ActivityRowSkeletonProps) {
	if (variant === "market") {
		// Custom skeleton matching TradeRow: avatar + inline text + timestamp + external link icon
		return (
			<div className="flex items-center gap-3 rounded-xl border border-transparent p-3">
				{/* Avatar - size-8 circle matching SimpleAvatar size="sm" */}
				<Skeleton className="size-8 shrink-0 rounded-full" />
				{/* Text content - single line with varied width */}
				<div className="min-w-0 flex-1">
					<Skeleton className="h-4 w-full max-w-[280px]" />
				</div>
				{/* Timestamp + icon - matches timeAgo text + ExternalLink container */}
				<div className="flex shrink-0 items-center gap-1.5">
					<Skeleton className="h-4 w-8" />
					<Skeleton className="size-6 rounded" />
				</div>
			</div>
		)
	}

	// Profile variant - matches ProfileActivityRow exactly:
	// - Card with rounded-xl, p-3 sm:p-4
	// - Buy/Sell: text-sm font-semibold = h-5
	// - Avatar: size-8 with rounded-md (SimpleAvatar sm + square)
	// - Market name: text-sm font-medium = h-5
	// - Details: text-xs = h-4
	// - Amount: text-sm font-semibold = h-5
	// - Time: text-xs = h-4 (desktop only)
	return (
		<div className="rounded-xl border border-border-subtle bg-bg-card p-3 sm:p-4">
			<div className="flex items-center gap-3">
				{/* Buy/Sell - text-sm font-semibold = h-5 */}
				<Skeleton className="h-5 w-8 shrink-0" />

				{/* Avatar - size-8 with rounded-md (sm + square variant) */}
				<Skeleton className="size-8 shrink-0 rounded-md" />

				{/* Market + Outcome */}
				<div className="min-w-0 flex-1">
					{/* Market name - text-sm font-medium = h-5 */}
					<Skeleton className="h-5 w-28" />
					{/* Details - text-xs = h-4 */}
					<Skeleton className="mt-1 h-4 w-36" />
				</div>

				{/* Amount - w-20 matches header */}
				<Skeleton className="h-5 w-20 shrink-0" />

				{/* Time + icon - w-24 matches header */}
				<div className="hidden w-24 shrink-0 items-center justify-end gap-1.5 sm:flex">
					<Skeleton className="h-4 w-12" />
					{/* External link icon placeholder - matches p-1 container with size-3.5 icon */}
					<div className="size-[22px]" />
				</div>
			</div>
		</div>
	)
}
